import { useEffect } from 'react';
import { useRealtimeNotificationStore } from '../store/realtimeNotificationStore';
import { useSessionStore } from '../store/sessionStore';
import { useUserStore } from '../store/userStore';
import { getAccessToken } from '../utils/authSession';
import {
  REALTIME_ANALYTICS_REFRESH_EVENTS,
  REALTIME_NOTIFICATION_EVENTS,
} from '../utils/realtimeEvents';
import { useMessageHubStore } from '../store/messageHubStore';
import { refreshCandidateAnalyticsRealtime } from '../utils/refreshAnalyticsRealtime';
import { refreshCalendarRealtime } from '../utils/refreshCalendarRealtime';
import { refreshDashboardRealtime } from '../utils/refreshDashboardRealtime';
import {
  disconnectSocket,
  getSocket,
  initSocket,
  isIncomingRealtimeMessage,
  isOwnQuestionRealtimeEcho,
  type V1SocketPayload,
} from '../utils/sockets';

/**
 * Single app-wide socket subscription: bell badge, toasts (via sockets.ts dispatch),
 * portfolio refresh when references complete, and analytics/prompts engagement refresh.
 */
export function useGlobalRealtime() {
  const sessionUserId = useSessionStore((s) => (s.currentUser as { id?: number } | null)?.id);
  const persistedUserId = useUserStore((s) => s.user?.id);
  const userId = sessionUserId ?? persistedUserId;

  useEffect(() => {
    const hasToken = Boolean(getAccessToken());
    if (!userId) {
      if (!hasToken) {
        disconnectSocket();
        useRealtimeNotificationStore.getState().reset();
      } else {
        void useRealtimeNotificationStore.getState().fetchInitial();
        getSocket();
      }
      return;
    }

    initSocket(userId);
    void useRealtimeNotificationStore.getState().fetchInitial();

    const socket = getSocket();
    const { pushFromPayload, fetchInitial } = useRealtimeNotificationStore.getState();

    const onSocketConnect = () => {
      void fetchInitial();
    };
    socket.on('connect', onSocketConnect);

    const onNotification = (payload: V1SocketPayload) => {
      pushFromPayload(payload);
    };

    const onNotificationAndDashboard = (payload: V1SocketPayload) => {
      onNotification(payload);
      refreshDashboardRealtime();
    };

    const refreshMessageHubLists = () => {
      const hub = useMessageHubStore.getState();
      void hub.fetchConversations({ force: true });
      void hub.fetchQuestions({ force: true });
      void hub.fetchContacts({ force: true });
      void hub.fetchRequests({ force: true });
    };

    const onNewMessage = (payload: V1SocketPayload) => {
      if (!isIncomingRealtimeMessage(payload, userId)) return;
      onNotificationAndDashboard(payload);
      refreshMessageHubLists();
    };

    const onReferenceConfirmed = (payload: V1SocketPayload) => {
      onNotificationAndDashboard(payload);
      void useSessionStore.getState().refreshPortfolio({ force: true });
    };

    const onAnalyticsRefresh = () => {
      void refreshCandidateAnalyticsRealtime();
      refreshDashboardRealtime();
    };

    const onProfileLike = (payload: V1SocketPayload) => {
      onNotificationAndDashboard(payload);
      void refreshCandidateAnalyticsRealtime();
    };

    const onMeetingEvent = (payload: V1SocketPayload) => {
      onNotificationAndDashboard(payload);
      refreshCalendarRealtime();
    };

    const onMessageHubEvent = (payload: V1SocketPayload) => {
      onNotificationAndDashboard(payload);
      refreshMessageHubLists();
    };

    const upsertQuestionFromRealtime = useMessageHubStore.getState().upsertQuestionFromRealtime;

    const onQuestionRealtime = (payload: V1SocketPayload) => {
      const eventName = String(payload.event || payload.type || '');
      const isOwnEcho =
        isOwnQuestionRealtimeEcho('questionAnswered', payload, userId) ||
        isOwnQuestionRealtimeEcho('question_updated', payload, userId) ||
        (eventName === 'newQuestion' && isOwnQuestionRealtimeEcho('newQuestion', payload, userId));

      const row = payload.data;
      if (row && typeof row === 'object') {
        upsertQuestionFromRealtime(row as Record<string, unknown>);
      }
      refreshMessageHubLists();

      if (isOwnEcho) {
        return;
      }

      onNotificationAndDashboard(payload);
    };

    const handlers: Record<string, (p: V1SocketPayload) => void> = {
      newMessage: onNewMessage,
      referenceConfirmed: onReferenceConfirmed,
      profileLike: onProfileLike,
      newQuestion: onQuestionRealtime,
      questionAnswered: onQuestionRealtime,
      question_updated: onQuestionRealtime,
      chatRequest: onMessageHubEvent,
      meeting_request_created: onMeetingEvent,
      meeting_request_updated: onMeetingEvent,
      meeting_request_rescheduled: onMeetingEvent,
    };

    for (const eventName of REALTIME_NOTIFICATION_EVENTS) {
      const handler = handlers[eventName] ?? onNotificationAndDashboard;
      socket.on(eventName, handler);
    }

    for (const eventName of REALTIME_ANALYTICS_REFRESH_EVENTS) {
      socket.on(eventName, onAnalyticsRefresh);
    }

    return () => {
      socket.off('connect', onSocketConnect);
      for (const eventName of REALTIME_NOTIFICATION_EVENTS) {
        const handler = handlers[eventName] ?? onNotificationAndDashboard;
        socket.off(eventName, handler);
      }
      for (const eventName of REALTIME_ANALYTICS_REFRESH_EVENTS) {
        socket.off(eventName, onAnalyticsRefresh);
      }
    };
  }, [userId]);
}
